import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Badge, Spinner, Button, Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../hooks/AuthProvider";
import { getApplicationsByApplicant, getListingById, addIncomeDocument, declineApplicantInvite, removeApplicant, addApplicantIncomeDocument } from "../../helpers/firestoreService";
import { getMyApplicantInvites, getApplicantsForApplication } from "../../helpers/applicantsApi";
import { startBackgroundScreening } from "../../helpers/checkrApi";
import {
  uploadIncomeDocument, getIncomeDocumentUrl,
  uploadApplicantIncomeDocument, getApplicantIncomeDocumentUrl,
} from "../../helpers/verificationApi";

const MAX_INCOME_DOCS = 6;
const MAX_INCOME_DOC_SIZE = 15 * 1024 * 1024; // 15MB

const STATUS_COLORS = {
  pending:  { bg: "#fef9ec", color: "#f1b44c", label: "Under Review" },
  approved: { bg: "#ecfdf5", color: "#34c38f", label: "Approved" },
  rejected: { bg: "#fdf2f2", color: "#f46a6a", label: "Declined" },
};

// Every adult occupant is screened directly by Checkr (emailed the moment
// the primary applicant pays) — this mirrors backgroundCheck.status rather
// than any separate "filled out their form" state, since there isn't one.
const COAPPLICANT_SCREENING = {
  invited:       { color: "#f1b44c", label: "Invited by Checkr" },
  in_progress:   { color: "#556ee6", label: "In Progress" },
  action_needed: { color: "#f1b44c", label: "Action Needed" },
  complete:      { color: "#34c38f", label: "Complete" },
  expired:       { color: "#f46a6a", label: "Expired" },
  error:         { color: "#f46a6a", label: "Error" },
};
const coApplicantStatusCfg = (s) => COAPPLICANT_SCREENING[s] || { color: "#74788d", label: "Screening Pending" };

const formatDob = (dob) => {
  if (!dob) return "—";
  const d = new Date(`${dob}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dob : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const MyApplicationsPage = () => {
  document.title = "My Applications | Porcher";
  const user = useAuthUser();
  const navigate = useNavigate();

  const [apps, setApps] = useState([]);
  const [listingMap, setListingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [screeningIncompleteNotice, setScreeningIncompleteNotice] = useState(false);
  const [screeningBusy, setScreeningBusy] = useState({});
  const [screeningError, setScreeningError] = useState({});
  const [incomeBusy, setIncomeBusy] = useState({});
  const [incomeError, setIncomeError] = useState({});
  const [viewingDocKey, setViewingDocKey] = useState(null);
  const [householdByApp, setHouseholdByApp] = useState({});
  const [householdError, setHouseholdError] = useState({});
  const [removeBusy, setRemoveBusy] = useState({});
  const [coScreeningBusy, setCoScreeningBusy] = useState({});
  const [coScreeningError, setCoScreeningError] = useState({});
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [inviteScreeningBusy, setInviteScreeningBusy] = useState({});
  const [inviteScreeningError, setInviteScreeningError] = useState({});
  const [declineBusy, setDeclineBusy] = useState({});
  const [declineError, setDeclineError] = useState({});
  const [inviteIncomeBusy, setInviteIncomeBusy] = useState({});
  const [inviteIncomeError, setInviteIncomeError] = useState({});
  const [inviteViewingDocKey, setInviteViewingDocKey] = useState(null);

  const loadInvites = async () => {
    if (!user?.uid) return;
    try {
      const results = await getMyApplicantInvites();
      setInvites(results);
      const ids = [...new Set(results.map((i) => i.listingId).filter(Boolean))];
      if (ids.length > 0) {
        const listings = await Promise.all(ids.map((id) => getListingById(id).catch(() => null)));
        setListingMap((prev) => {
          const next = { ...prev };
          ids.forEach((id, i) => { if (listings[i]) next[id] = listings[i]; });
          return next;
        });
      }
    } catch (e) {
      console.error("Failed to load co-applicant invites:", e);
    } finally {
      setInvitesLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        const results = await getApplicationsByApplicant(user.uid);
        setApps(results);
        // Fetch listing details for each unique listingId
        const ids = [...new Set(results.map((a) => a.listingId).filter(Boolean))];
        const listings = await Promise.all(ids.map((id) => getListingById(id).catch(() => null)));
        const map = {};
        ids.forEach((id, i) => { if (listings[i]) map[id] = listings[i]; });
        setListingMap(map);

        // Household summary (minorsCount already lives on the app itself;
        // other adult occupants live in the applicants sub-collection). Track
        // per-application load failures separately from "genuinely no
        // co-applicants" — a silent [] fallback here previously made a
        // permissions/index error look identical to an empty household.
        const households = await Promise.all(
          results.map((a) => getApplicantsForApplication(a.id).catch((e) => {
            console.error(`Failed to load household for application ${a.id}:`, e);
            return null;
          }))
        );
        const householdMap = {};
        const householdErrMap = {};
        results.forEach((a, i) => {
          if (households[i] === null) { householdMap[a.id] = []; householdErrMap[a.id] = true; }
          else householdMap[a.id] = households[i];
        });
        setHouseholdByApp(householdMap);
        setHouseholdError(householdErrMap);
      } catch (e) {
        console.error("Failed to load applications:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    loadInvites();
  }, [user?.uid]);

  useEffect(() => {
    if (sessionStorage.getItem("porcher_coapplicant_screening_incomplete")) {
      setScreeningIncompleteNotice(true);
      sessionStorage.removeItem("porcher_coapplicant_screening_incomplete");
    }
  }, []);

  const handleStartScreening = async (appId) => {
    setScreeningError((prev) => ({ ...prev, [appId]: "" }));
    setScreeningBusy((prev) => ({ ...prev, [appId]: true }));
    try {
      const result = await startBackgroundScreening(appId);
      setApps((prev) => prev.map((a) => (a.id === appId ? {
        ...a,
        backgroundCheck: {
          ...(a.backgroundCheck || {}),
          status: result.status,
          invitationUrl: result.invitationUrl,
        },
      } : a)));
    } catch (e) {
      console.error("Start background screening error:", e);
      setScreeningError((prev) => ({ ...prev, [appId]: "Couldn't start the background screening. Please try again." }));
    } finally {
      setScreeningBusy((prev) => ({ ...prev, [appId]: false }));
    }
  };

  // Co-applicant screenings are normally created server-side the instant the
  // primary pays — this is just a manual retry/refresh for when that failed
  // (e.g. a bad property address) or the webhook hasn't run yet.
  const handleRefreshInviteScreening = async (inv) => {
    setInviteScreeningError((prev) => ({ ...prev, [inv.id]: "" }));
    setInviteScreeningBusy((prev) => ({ ...prev, [inv.id]: true }));
    try {
      const result = await startBackgroundScreening(inv.applicationId, {}, inv.id);
      setInvites((prev) => prev.map((i) => (i.id === inv.id ? {
        ...i,
        backgroundCheck: {
          ...(i.backgroundCheck || {}),
          status: result.status,
          invitationUrl: result.invitationUrl,
        },
      } : i)));
    } catch (e) {
      console.error("Refresh co-applicant screening error:", e);
      setInviteScreeningError((prev) => ({ ...prev, [inv.id]: "Couldn't refresh the screening status. Please try again." }));
    } finally {
      setInviteScreeningBusy((prev) => ({ ...prev, [inv.id]: false }));
    }
  };

  const handleDeclineInvite = async (inv) => {
    if (!window.confirm("Decline this co-applicant invitation? This can't be undone.")) return;
    setDeclineError((prev) => ({ ...prev, [inv.id]: "" }));
    setDeclineBusy((prev) => ({ ...prev, [inv.id]: true }));
    try {
      await declineApplicantInvite(inv.applicationId, inv.id);
      setInvites((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: "declined" } : i)));
    } catch (e) {
      console.error("Decline invite error:", e);
      setDeclineError((prev) => ({ ...prev, [inv.id]: "Couldn't decline this invitation. Please try again." }));
    } finally {
      setDeclineBusy((prev) => ({ ...prev, [inv.id]: false }));
    }
  };

  // Removes a co-applicant from the primary applicant's own household card.
  const handleRemoveApplicant = async (applicationId, co) => {
    if (!window.confirm(`Remove ${co.name || co.email} from this application?`)) return;
    setRemoveBusy((prev) => ({ ...prev, [co.id]: true }));
    try {
      await removeApplicant(applicationId, co.id);
      setHouseholdByApp((prev) => ({
        ...prev,
        [applicationId]: (prev[applicationId] || []).filter((c) => c.id !== co.id),
      }));
    } catch (e) {
      console.error("Remove co-applicant error:", e);
      window.alert("Couldn't remove this co-applicant. Please try again.");
    } finally {
      setRemoveBusy((prev) => ({ ...prev, [co.id]: false }));
    }
  };

  // Screenings for additional adult occupants are normally created
  // server-side the instant the primary pays — this is the primary's own
  // manual retry/refresh button on their Household card.
  const handleRetryCoScreening = async (applicationId, co) => {
    setCoScreeningError((prev) => ({ ...prev, [co.id]: "" }));
    setCoScreeningBusy((prev) => ({ ...prev, [co.id]: true }));
    try {
      const result = await startBackgroundScreening(applicationId, {}, co.id);
      setHouseholdByApp((prev) => ({
        ...prev,
        [applicationId]: (prev[applicationId] || []).map((c) => (c.id === co.id ? {
          ...c,
          backgroundCheck: { ...(c.backgroundCheck || {}), status: result.status, invitationUrl: result.invitationUrl },
        } : c)),
      }));
    } catch (e) {
      console.error("Retry co-applicant screening error:", e);
      setCoScreeningError((prev) => ({ ...prev, [co.id]: "Couldn't start/refresh this screening." }));
    } finally {
      setCoScreeningBusy((prev) => ({ ...prev, [co.id]: false }));
    }
  };

  const handleUploadIncomeDoc = async (app, file) => {
    if (!file) return;
    setIncomeError((prev) => ({ ...prev, [app.id]: "" }));
    if ((app.incomeDocuments || []).length >= MAX_INCOME_DOCS) {
      setIncomeError((prev) => ({ ...prev, [app.id]: `You can upload up to ${MAX_INCOME_DOCS} documents.` }));
      return;
    }
    if (file.size > MAX_INCOME_DOC_SIZE) {
      setIncomeError((prev) => ({ ...prev, [app.id]: "That file is too large (max 15MB)." }));
      return;
    }
    setIncomeBusy((prev) => ({ ...prev, [app.id]: true }));
    try {
      const docMeta = await uploadIncomeDocument(app.id, file);
      await addIncomeDocument(app.id, docMeta);
      setApps((prev) => prev.map((a) => (a.id === app.id
        ? { ...a, incomeDocuments: [...(a.incomeDocuments || []), docMeta] }
        : a)));
    } catch (e) {
      console.error("Upload income document error:", e);
      setIncomeError((prev) => ({ ...prev, [app.id]: "Couldn't upload that file. Please try again." }));
    } finally {
      setIncomeBusy((prev) => ({ ...prev, [app.id]: false }));
    }
  };

  const handleViewIncomeDoc = async (appId, key) => {
    setViewingDocKey(key);
    try {
      const url = await getIncomeDocumentUrl(appId, key);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Get income document url error:", e);
      setIncomeError((prev) => ({ ...prev, [appId]: "Couldn't open that document. Please try again." }));
    } finally {
      setViewingDocKey(null);
    }
  };

  // Same proof-of-income upload/view flow as the primary applicant's own,
  // scoped to the co-applicant's own sub-doc (their "invite" card).
  const MAX_INVITE_INCOME_DOCS = MAX_INCOME_DOCS;
  const handleUploadInviteIncomeDoc = async (inv, file) => {
    if (!file) return;
    setInviteIncomeError((prev) => ({ ...prev, [inv.id]: "" }));
    if ((inv.incomeDocuments || []).length >= MAX_INVITE_INCOME_DOCS) {
      setInviteIncomeError((prev) => ({ ...prev, [inv.id]: `You can upload up to ${MAX_INVITE_INCOME_DOCS} documents.` }));
      return;
    }
    if (file.size > MAX_INCOME_DOC_SIZE) {
      setInviteIncomeError((prev) => ({ ...prev, [inv.id]: "That file is too large (max 15MB)." }));
      return;
    }
    setInviteIncomeBusy((prev) => ({ ...prev, [inv.id]: true }));
    try {
      const docMeta = await uploadApplicantIncomeDocument(inv.applicationId, inv.id, file);
      await addApplicantIncomeDocument(inv.applicationId, inv.id, docMeta);
      setInvites((prev) => prev.map((i) => (i.id === inv.id
        ? { ...i, incomeDocuments: [...(i.incomeDocuments || []), docMeta] }
        : i)));
    } catch (e) {
      console.error("Upload co-applicant income document error:", e);
      setInviteIncomeError((prev) => ({ ...prev, [inv.id]: "Couldn't upload that file. Please try again." }));
    } finally {
      setInviteIncomeBusy((prev) => ({ ...prev, [inv.id]: false }));
    }
  };

  const handleViewInviteIncomeDoc = async (inv, key) => {
    setInviteViewingDocKey(key);
    try {
      const url = await getApplicantIncomeDocumentUrl(inv.applicationId, inv.id, key);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Get co-applicant income document url error:", e);
      setInviteIncomeError((prev) => ({ ...prev, [inv.id]: "Couldn't open that document. Please try again." }));
    } finally {
      setInviteViewingDocKey(null);
    }
  };

  return (
    <div className="page-content">
      <Container>
        <div className="mb-4">
          <h3 className="fw-bold mb-1">My Applications</h3>
          <p className="text-muted mb-0">Track the status of your rental applications.</p>
        </div>

        {screeningIncompleteNotice && (
          <Alert color="warning" className="py-2 small" toggle={() => setScreeningIncompleteNotice(false)}>
            <i className="bx bx-error-circle me-1" />
            We couldn't automatically start screening for one or more of your co-applicants.
            Use the "Start / Resend Screening" link under their name in the Household section below.
          </Alert>
        )}

        {loading || invitesLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : apps.length === 0 && invites.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="py-5 text-center">
              <i className="bx bx-file-blank text-muted" style={{ fontSize: "3rem" }} />
              <h5 className="mt-3 mb-2">No applications yet</h5>
              <p className="text-muted mb-0">When you apply for a rental, your applications will appear here.</p>
            </CardBody>
          </Card>
        ) : (
          <Row>
            {apps.map((app) => {
              const listing = listingMap[app.listingId];
              const statusCfg = STATUS_COLORS[app.status] || STATUS_COLORS.pending;
              const submitted = app.submittedAt
                ? new Date(app.submittedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                : "—";
              const applicantName = [app.firstName, app.lastName].filter(Boolean).join(" ") || app.name;

              return (
                <Col lg={6} key={app.id} className="mb-3">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="fw-semibold mb-1">
                            {listing?.title || "Rental Unit"}
                          </h5>
                          {listing?.address && (
                            <p className="text-muted small mb-0">
                              <i className="bx bx-map me-1" />{listing.address}
                            </p>
                          )}
                        </div>
                        <span
                          className="px-2 py-1 rounded-pill small fw-semibold ms-2 flex-shrink-0"
                          style={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.color}44` }}
                        >
                          {statusCfg.label}
                        </span>
                      </div>

                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {listing?.monthlyRent > 0 && (
                          <Badge color="light" className="text-dark fw-normal">
                            ${listing.monthlyRent.toLocaleString()}/mo
                          </Badge>
                        )}
                        {listing?.bedrooms && (
                          <Badge color="light" className="text-dark fw-normal">
                            {listing.bedrooms} bed
                          </Badge>
                        )}
                        {listing?.bathrooms && (
                          <Badge color="light" className="text-dark fw-normal">
                            {listing.bathrooms} bath
                          </Badge>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted">
                          <i className="bx bx-calendar me-1" />Submitted {submitted}
                        </small>
                        {listing && (
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => navigate(`/rentals/${app.listingId}`)}
                          >
                            View Listing
                          </Button>
                        )}
                      </div>

                      {/* What you submitted (everything except SSN, which is never stored) */}
                      <div className="border-top pt-3 mb-3">
                        <h6 className="fw-semibold mb-2">
                          <i className="bx bx-id-card me-1" />Application Details
                        </h6>
                        <Row className="g-2 small">
                          <Col xs={6}><span className="text-muted">Name</span><div className="fw-medium">{applicantName || "—"}</div></Col>
                          <Col xs={6}><span className="text-muted">Phone</span><div className="fw-medium">{app.phone || "—"}</div></Col>
                          <Col xs={6}><span className="text-muted">Date of Birth</span><div className="fw-medium">{formatDob(app.dob)}</div></Col>
                          <Col xs={6}><span className="text-muted">Employer</span><div className="fw-medium">{app.employer || "—"}</div></Col>
                          <Col xs={6}><span className="text-muted">Monthly Income</span><div className="fw-medium">{app.monthlyIncome ? `$${Number(app.monthlyIncome).toLocaleString()}` : "—"}</div></Col>
                        </Row>
                      </div>

                      {/* Other household occupants — the PM sees the same list */}
                      {(app.minorsCount > 0 || (householdByApp[app.id] || []).length > 0 || householdError[app.id]) && (
                        <div className="border-top pt-3 mb-3">
                          <h6 className="fw-semibold mb-2">
                            <i className="bx bx-group me-1" />Household
                          </h6>
                          {householdError[app.id] && (
                            <Alert color="warning" className="py-2 small mb-2">
                              Couldn't load your co-applicants' details. Refresh the page to try again.
                            </Alert>
                          )}
                          {app.minorsCount > 0 && (
                            <p className="small mb-2">
                              <span className="text-muted">Occupants under 18:</span> <span className="fw-medium">{app.minorsCount}</span>
                            </p>
                          )}
                          {(householdByApp[app.id] || []).map((co) => {
                            const declined = co.status === "declined";
                            const cfg = declined
                              ? { color: "#f46a6a", label: "Declined" }
                              : coApplicantStatusCfg(co.backgroundCheck?.status);
                            return (
                              <div key={co.id} className="small mb-2 pb-2 border-bottom">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <div className="fw-medium">
                                      {co.name || co.email} {co.relationship && <span className="text-muted">({co.relationship})</span>}
                                    </div>
                                    <div className="text-muted">{co.email || "—"}</div>
                                    <div className="text-muted">{co.phone || "—"} · DOB {formatDob(co.dob)}</div>
                                    <div className="text-muted">
                                      {co.employer || "—"}
                                      {" · "}{co.monthlyIncome ? `$${Number(co.monthlyIncome).toLocaleString()}/mo` : "—"}
                                    </div>
                                  </div>
                                  <div className="text-end flex-shrink-0 ms-2">
                                    <span className="d-inline-block px-2 py-1 rounded-pill fw-semibold mb-1" style={{ background: `${cfg.color}1a`, color: cfg.color }}>
                                      {cfg.label}
                                    </span>
                                    <div>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        disabled={removeBusy[co.id]}
                                        onClick={() => handleRemoveApplicant(app.id, co)}
                                      >
                                        {removeBusy[co.id] ? <Spinner size="sm" /> : "Remove"}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                {coScreeningError[co.id] && (
                                  <Alert color="danger" className="py-1 px-2 small mt-2 mb-0">{coScreeningError[co.id]}</Alert>
                                )}
                                {!declined && co.backgroundCheck?.status !== "complete" && (
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 mt-1"
                                    disabled={coScreeningBusy[co.id]}
                                    onClick={() => handleRetryCoScreening(app.id, co)}
                                  >
                                    {coScreeningBusy[co.id] ? <Spinner size="sm" /> : co.backgroundCheck?.status ? "Refresh Status" : "Start / Resend Screening"}
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}


                      <div className="border-top pt-3 mb-3">
                        <h6 className="fw-semibold mb-2">
                          <i className="bx bx-file me-1" />Proof of Income
                        </h6>
                        {incomeError[app.id] && (
                          <Alert color="danger" className="py-2 small mb-2">{incomeError[app.id]}</Alert>
                        )}
                        {(app.incomeDocuments || []).length === 0 ? (
                          <p className="text-muted small mb-2">Upload your last 3 months of pay stubs or bank statements.</p>
                        ) : (
                          <div className="d-flex flex-column gap-1 mb-2">
                            {app.incomeDocuments.map((d) => (
                              <div key={d.key} className="d-flex align-items-center justify-content-between small border rounded px-2 py-1">
                                <span className="text-truncate"><i className="bx bx-file-blank me-1 text-muted" />{d.filename}</span>
                                <Button
                                  color="link"
                                  size="sm"
                                  className="py-0"
                                  disabled={viewingDocKey === d.key}
                                  onClick={() => handleViewIncomeDoc(app.id, d.key)}
                                >
                                  {viewingDocKey === d.key ? <Spinner size="sm" /> : "View"}
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(app.incomeDocuments || []).length < MAX_INCOME_DOCS && (
                          <div>
                            <input
                              type="file"
                              id={`income-upload-${app.id}`}
                              accept="image/*,.pdf"
                              className="d-none"
                              onChange={(e) => { handleUploadIncomeDoc(app, e.target.files[0]); e.target.value = ""; }}
                            />
                            <Button
                              color="light"
                              size="sm"
                              disabled={incomeBusy[app.id]}
                              onClick={() => document.getElementById(`income-upload-${app.id}`).click()}
                            >
                              {incomeBusy[app.id] ? <Spinner size="sm" /> : <><i className="bx bx-upload me-1" />Upload Document</>}
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="border-top pt-3">
                        <h6 className="fw-semibold mb-1">
                          <i className="bx bx-shield-quarter me-1" />Background Screening
                        </h6>
                        {screeningError[app.id] && (
                          <Alert color="danger" className="py-2 small mb-2">{screeningError[app.id]}</Alert>
                        )}
                        {!app.backgroundCheck?.status && (
                          <>
                            <p className="text-muted small mb-2">
                              Screening is completed securely through Checkr. If you didn't add your SSN on
                              the application, you'll be asked for it directly on Checkr's site instead.
                            </p>
                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={screeningBusy[app.id]}
                              onClick={() => handleStartScreening(app.id)}
                            >
                              {screeningBusy[app.id] ? <Spinner size="sm" /> : "Start Background Screening"}
                            </Button>
                          </>
                        )}
                        {app.backgroundCheck?.status === "invited" && (
                          <>
                            <p className="text-muted small mb-2">
                              Your screening invitation is ready. Complete it on Checkr's secure site.
                            </p>
                            {app.backgroundCheck.invitationUrl ? (
                              <Button
                                color="primary"
                                size="sm"
                                tag="a"
                                href={app.backgroundCheck.invitationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Continue to Checkr
                              </Button>
                            ) : (
                              <Button
                                color="light"
                                size="sm"
                                disabled={screeningBusy[app.id]}
                                onClick={() => handleStartScreening(app.id)}
                              >
                                {screeningBusy[app.id] ? <Spinner size="sm" /> : "Refresh Status"}
                              </Button>
                            )}
                          </>
                        )}
                        {(app.backgroundCheck?.status === "in_progress" || app.backgroundCheck?.status === "action_needed") && (
                          <>
                            <p className="text-muted small mb-2">
                              <i className="bx bx-time-five me-1" />
                              {app.backgroundCheck.status === "action_needed"
                                ? "Checkr needs additional information from you — check your email."
                                : "Background screening in progress."}
                            </p>
                            <Button
                              color="light"
                              size="sm"
                              disabled={screeningBusy[app.id]}
                              onClick={() => handleStartScreening(app.id)}
                            >
                              {screeningBusy[app.id] ? <Spinner size="sm" /> : "Refresh Status"}
                            </Button>
                          </>
                        )}
                        {app.backgroundCheck?.status === "complete" && (
                          <p className="text-success small mb-0">
                            <i className="bx bx-check-circle me-1" />Background screening complete.
                          </p>
                        )}
                        {(app.backgroundCheck?.status === "expired" || app.backgroundCheck?.status === "error") && (
                          <>
                            <p className="text-muted small mb-2">
                              {app.backgroundCheck.status === "expired"
                                ? "Your screening invitation expired."
                                : "Something went wrong with your background screening."}
                            </p>
                            <Button
                              color="primary"
                              outline
                              size="sm"
                              disabled={screeningBusy[app.id]}
                              onClick={() => handleStartScreening(app.id)}
                            >
                              {screeningBusy[app.id] ? <Spinner size="sm" /> : "Retry"}
                            </Button>
                          </>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}

            {invites.map((inv) => {
                const listing = listingMap[inv.listingId];
                const declined = inv.status === "declined";
                const appStatusCfg = STATUS_COLORS[inv.applicationStatus] || STATUS_COLORS.pending;
                return (
                  <Col lg={6} key={inv.id} className="mb-3">
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody className="p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h5 className="fw-semibold mb-1">{listing?.title || "Rental Unit"}</h5>
                            {listing?.address && (
                              <p className="text-muted small mb-0"><i className="bx bx-map me-1" />{listing.address}</p>
                            )}
                          </div>
                          <span
                            className="px-2 py-1 rounded-pill small fw-semibold ms-2 flex-shrink-0"
                            style={{ background: appStatusCfg.bg, color: appStatusCfg.color, border: `1px solid ${appStatusCfg.color}44` }}
                          >
                            {appStatusCfg.label}
                          </span>
                        </div>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {listing?.monthlyRent > 0 && (
                            <Badge color="light" className="text-dark fw-normal">${listing.monthlyRent.toLocaleString()}/mo</Badge>
                          )}
                          {listing?.bedrooms && <Badge color="light" className="text-dark fw-normal">{listing.bedrooms} bed</Badge>}
                          {listing?.bathrooms && <Badge color="light" className="text-dark fw-normal">{listing.bathrooms} bath</Badge>}
                        </div>

                        <Alert color="info" className="py-2 small mb-3">
                          <i className="bx bx-user-plus me-1" />
                          You're a co-applicant on this application
                          {inv.relationship ? ` (as ${inv.relationship})` : ""}
                          {inv.invitedByName ? ` — invited by ${inv.invitedByName}.` : "."}
                        </Alert>

                        {/* Their own info, same fields collected for every co-applicant */}
                        <div className="border-top pt-3 mb-3">
                          <h6 className="fw-semibold mb-2"><i className="bx bx-id-card me-1" />Application Details</h6>
                          <Row className="g-2 small">
                            <Col xs={12}><span className="text-muted">Name</span><div className="fw-medium">{inv.name || [inv.firstName, inv.lastName].filter(Boolean).join(" ") || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Email</span><div className="fw-medium text-truncate">{inv.email || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Phone</span><div className="fw-medium">{inv.phone || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Date of Birth</span><div className="fw-medium">{formatDob(inv.dob)}</div></Col>
                            <Col xs={6}><span className="text-muted">Relationship</span><div className="fw-medium">{inv.relationship || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Employer</span><div className="fw-medium">{inv.employer || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Monthly Income</span><div className="fw-medium">{inv.monthlyIncome ? `$${Number(inv.monthlyIncome).toLocaleString()}` : "—"}</div></Col>
                          </Row>
                        </div>

                        {/* Proof of income — same self-upload pattern as the primary applicant's own */}
                        <div className="border-top pt-3 mb-3">
                          <h6 className="fw-semibold mb-2"><i className="bx bx-file me-1" />Proof of Income <span className="text-muted fw-normal">(optional)</span></h6>
                          {inviteIncomeError[inv.id] && (
                            <Alert color="danger" className="py-2 small mb-2">{inviteIncomeError[inv.id]}</Alert>
                          )}
                          {(inv.incomeDocuments || []).length === 0 ? (
                            <p className="text-muted small mb-2">Upload your last 3 months of pay stubs or bank statements.</p>
                          ) : (
                            <div className="d-flex flex-column gap-1 mb-2">
                              {inv.incomeDocuments.map((d) => (
                                <div key={d.key} className="d-flex align-items-center justify-content-between small border rounded px-2 py-1">
                                  <span className="text-truncate"><i className="bx bx-file-blank me-1 text-muted" />{d.filename}</span>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="py-0"
                                    disabled={inviteViewingDocKey === d.key}
                                    onClick={() => handleViewInviteIncomeDoc(inv, d.key)}
                                  >
                                    {inviteViewingDocKey === d.key ? <Spinner size="sm" /> : "View"}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          {(inv.incomeDocuments || []).length < MAX_INCOME_DOCS && (
                            <div>
                              <input
                                type="file"
                                id={`invite-income-upload-${inv.id}`}
                                accept="image/*,.pdf"
                                className="d-none"
                                onChange={(e) => { handleUploadInviteIncomeDoc(inv, e.target.files[0]); e.target.value = ""; }}
                              />
                              <Button
                                color="light"
                                size="sm"
                                disabled={inviteIncomeBusy[inv.id]}
                                onClick={() => document.getElementById(`invite-income-upload-${inv.id}`).click()}
                              >
                                {inviteIncomeBusy[inv.id] ? <Spinner size="sm" /> : <><i className="bx bx-upload me-1" />Upload Document</>}
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Main applicant's details, so the co-applicant knows who they're applying with */}
                        <div className="border-top pt-3 mb-3">
                          <h6 className="fw-semibold mb-2"><i className="bx bx-user me-1" />Main Applicant</h6>
                          <Row className="g-2 small">
                            <Col xs={12}><span className="text-muted">Name</span><div className="fw-medium">{inv.primaryApplicant?.name || inv.invitedByName || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Email</span><div className="fw-medium text-truncate">{inv.primaryApplicant?.email || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Phone</span><div className="fw-medium">{inv.primaryApplicant?.phone || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Date of Birth</span><div className="fw-medium">{formatDob(inv.primaryApplicant?.dob)}</div></Col>
                            <Col xs={6}><span className="text-muted">Employer</span><div className="fw-medium">{inv.primaryApplicant?.employer || "—"}</div></Col>
                            <Col xs={6}><span className="text-muted">Monthly Income</span><div className="fw-medium">{inv.primaryApplicant?.monthlyIncome ? `$${Number(inv.primaryApplicant.monthlyIncome).toLocaleString()}` : "—"}</div></Col>
                          </Row>
                        </div>

                        {declineError[inv.id] && (
                          <Alert color="danger" className="py-2 small mb-2">{declineError[inv.id]}</Alert>
                        )}

                        {declined ? (
                          <p className="text-muted small mb-0 border-top pt-3">
                            <i className="bx bx-block me-1" />You declined this application.
                          </p>
                        ) : (
                          <div className="border-top pt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="fw-semibold mb-0"><i className="bx bx-shield-quarter me-1" />Background Screening</h6>
                              <Button
                                color="link"
                                size="sm"
                                className="text-danger p-0"
                                disabled={declineBusy[inv.id]}
                                onClick={() => handleDeclineInvite(inv)}
                              >
                                {declineBusy[inv.id] ? <Spinner size="sm" /> : "Decline"}
                              </Button>
                            </div>

                            {inviteScreeningError[inv.id] && (
                              <Alert color="danger" className="py-2 small mb-2">{inviteScreeningError[inv.id]}</Alert>
                            )}

                            {inv.backgroundCheck?.status === "complete" ? (
                              <p className="text-success small mb-0">
                                <i className="bx bx-check-circle me-1" />Background screening complete.
                              </p>
                            ) : inv.backgroundCheck?.invitationUrl ? (
                              <Button
                                color="primary"
                                size="sm"
                                tag="a"
                                href={inv.backgroundCheck.invitationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Continue to Checkr
                              </Button>
                            ) : (
                              <>
                                <p className="text-muted small mb-2">
                                  {inv.backgroundCheck?.status
                                    ? "Your screening is being processed."
                                    : "Check your email for a message from Checkr, or refresh below."}
                                </p>
                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={inviteScreeningBusy[inv.id]}
                                  onClick={() => handleRefreshInviteScreening(inv)}
                                >
                                  {inviteScreeningBusy[inv.id] ? <Spinner size="sm" /> : "Refresh Status"}
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
          </Row>
        )}
      </Container>
    </div>
  );
};


export default MyApplicationsPage;
